const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
const WS_BASE_URL = process.env.REACT_APP_WS_BASE_URL || 'ws://localhost:8080';

export interface ApiResponse<T = any> {
  data?: T;
  error?: string;
}

class ApiService {
  private sessionId: string;

  constructor() {
    this.sessionId = this.generateSessionId();
  }

  private generateSessionId(): string {
    return crypto.randomUUID();
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    timeout: number = 10000 // Default 10 second timeout
  ): Promise<ApiResponse<T>> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          'SessionId': this.sessionId,
          ...options.headers,
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { data };
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.error('API request timeout:', endpoint);
        return { error: 'Request timeout' };
      }
      console.error('API request failed:', error);
      return { error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  async uploadFile(file: File): Promise<ApiResponse<{
    filename: string;
    size: number;
    message: string;
  }>> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_BASE_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'SessionId': this.sessionId,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { data };
    } catch (error) {
      console.error('File upload failed:', error);
      return { error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  async getInvoices(): Promise<ApiResponse<string[]>> {
    return this.makeRequest<string[]>('/api/invoice');
  }

  async getInvoiceFields(fileName: string): Promise<ApiResponse<{
    fields: {
      headers: Array<{ key: string; value: string | number }>;
      items: Array<{ index: number; key: string; value: string | number }>;
    };
  }>> {
    return this.makeRequest(`/api/invoice/${encodeURIComponent(fileName)}/fields`);
  }

  async updateInvoiceFields(
    fileName: string,
    fields: {
      headers: Array<{ key: string; value: string | number }>;
      items: Array<{ index: number; key: string; value: string | number }>;
    }
  ): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/invoice/${encodeURIComponent(fileName)}/fields`, {
      method: 'POST',
      body: JSON.stringify({ fields }),
    });
  }

  async getInvoiceFeedback(fileName: string): Promise<ApiResponse<{
    feedback: {
      headers: Array<{ key: string; feedback: string }>;
      items: Array<{ index: number; key: string; feedback: string }>;
    };
  }>> {
    return this.makeRequest(`/api/invoice/${encodeURIComponent(fileName)}/feedback`);
  }

  async submitInvoiceFeedback(
    fileName: string,
    feedback: {
      headers: Array<{ key: string; feedback: string }>;
      items: Array<{ index: number; key: string; feedback: string }>;
    }
  ): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/invoice/${encodeURIComponent(fileName)}/feedback`, {
      method: 'POST',
      body: JSON.stringify({ feedback }),
    });
  }

  async triggerWorkflow(
    fileName: string,
    query: string,
    userId: string
  ): Promise<ApiResponse<{
    results: Array<{ role: string; content: string }>;
  }>> {
    return this.makeRequest(`/api/workflow/${encodeURIComponent(fileName)}`, {
      method: 'POST',
      body: JSON.stringify({ query, user_id: userId }),
    }, 30000); // 30 second timeout for workflow trigger
  }

  async getWorkflowStatus(fileName: string): Promise<ApiResponse<{
    id: string;
    workflow_id: string;
    workflow_type: string;
    invoice_id: string;
    invoice_number: string;
    status: string;
    last_updated: string;
    created_at: string;
    next_action_due: string;
    hitl_requester_email: string;
  }>> {
    return this.makeRequest(`/api/workflow/${encodeURIComponent(fileName)}/status`);
  }

  async updateWorkflowStatus(
    fileName: string,
    status: string
  ): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/workflow/${encodeURIComponent(fileName)}/status`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    });
  }

  async continueWorkflow(
    fileName: string,
    query: string,
    userId: string
  ): Promise<ApiResponse<{
    results: Array<{ role: string; content: string }>;
  }>> {
    return this.makeRequest(`/api/workflow/${encodeURIComponent(fileName)}/continue`, {
      method: 'POST',
      body: JSON.stringify({ query, user_id: userId }),
    });
  }

  getWebSocketUrl(): string {
    const uniqueString = Math.floor(10000 + Math.random() * 90000).toString();
    return `${WS_BASE_URL}/ws/${encodeURIComponent(uniqueString)}`;
  }

  getSessionId(): string {
    return this.sessionId;
  }

  // Agent data fetching methods
  async getFieldExtractorData(invoiceId: string): Promise<ApiResponse<{
    id: string;
    fields: string;
  }>> {
    return this.makeRequest(`/api/agents/field_extractor/${encodeURIComponent(invoiceId)}`);
  }

  async getFieldExtractorVisionData(invoiceId: string): Promise<ApiResponse<{
    id: string;
    fields: string;
  }>> {
    return this.makeRequest(`/api/agents/field_extractor_vision/${encodeURIComponent(invoiceId)}`);
  }

  async getCriticalFieldsData(invoiceId: string): Promise<ApiResponse<{
    id: string;
    fields: string;
  }>> {
    return this.makeRequest(`/api/agents/critical_fields/${encodeURIComponent(invoiceId)}`);
  }

  async getInvoiceTypeClassifierData(invoiceId: string): Promise<ApiResponse<{
    id: string;
    category: string;
    invoice_type: string[];
    invoice_category: string[];
    reason: string;
    confidence: string;
  }>> {
    return this.makeRequest(`/api/agents/invoice_type_classifier/${encodeURIComponent(invoiceId)}`);
  }

  async getFieldLevelClassifierData(invoiceId: string): Promise<ApiResponse<{
    id: string;
    region: string;
    country: string;
    type: string;
    category: string;
    fields: string;
  }>> {
    return this.makeRequest(`/api/agents/field_level_classifier/${encodeURIComponent(invoiceId)}`);
  }

  async getFieldDeriverData(invoiceId: string): Promise<ApiResponse<{
    id: string;
    fields: string;
    errors: string;
  }>> {
    return this.makeRequest(`/api/agents/field_deriver/${encodeURIComponent(invoiceId)}`);
  }

  async getAnomalyDetectorData(invoiceId: string): Promise<ApiResponse<{
    id: string;
    fields: string;
    anomalies: string;
  }>> {
    return this.makeRequest(`/api/agents/anomaly_detector/${encodeURIComponent(invoiceId)}`);
  }

  async submitFeedback(fileName: string, feedbackData: any): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/invoice/${encodeURIComponent(fileName)}/feedback`, {
      method: 'POST',
      body: JSON.stringify(feedbackData),
    });
  }

  async getValidationData(invoiceId: string): Promise<ApiResponse<{
    id: string;
    vmd: string;
    po_header: string;
    po_line_i: string;
    exceptions: string;
  }>> {
    return this.makeRequest(`/api/agents/validation/${encodeURIComponent(invoiceId)}`);
  }

  async getWatchTowerData(invoiceId: string): Promise<ApiResponse<{
    id: string;
    category: string;
    reason: string;
    filename: string;
    time_stamp: string;
    predicted_response_time: number | null;
  }>> {
    return this.makeRequest(`/api/agents/watch_tower/${encodeURIComponent(invoiceId)}`);
  }

  async getExceptionsData(invoiceId: string, sessionId: string): Promise<ApiResponse<{
    id: string;
    exception_type: string;
  }>> {
    return this.makeRequest(`/api/agents/exceptions/${encodeURIComponent(invoiceId)}-${encodeURIComponent(sessionId)}`);
  }

  async getAgentErrors(invoiceId: string, sessionId: string): Promise<ApiResponse<{
    id: string;
    errors: string;
  }>> {
    return this.makeRequest(`/api/agents/errors/${encodeURIComponent(invoiceId)}-${encodeURIComponent(sessionId)}`);
  }

  async getWorkflowData(fileName: string): Promise<ApiResponse<{
    workflow_status: {
      id: string;
      workflow_type: string;
      invoice_id: string;
      invoice_number: string;
      status: string;
      last_updated: string;
      created_at: string;
      next_action_due: string;
      requester_email: string;
      action_time: string;
      notes: string;
    } | null;
    reminders: Array<{
      id: string;
      workflow_id: string;
      reminder_number: number;
      reminder_time: string;
      reminder_message: string;
    }>;
  }>> {
    return this.makeRequest(`/api/workflow/${encodeURIComponent(fileName)}/data`);
  }

  async uploadScreenshot(screenshot: File): Promise<ApiResponse<{
    url: string;
    filename: string;
    size: number;
  }>> {
    try {
      const formData = new FormData();
      formData.append('screenshot', screenshot);

      const response = await fetch(`${API_BASE_URL}/api/upload-screenshot`, {
        method: 'POST',
        headers: {
          'SessionId': this.sessionId,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { data };
    } catch (error) {
      console.error('Screenshot upload failed:', error);
      return { error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  // Admin Portal API methods
  async getFieldConfig(): Promise<ApiResponse<{
    field_config: Array<{
      id: string;
      field: string;
      description: string;
      is_item: boolean;
      is_critical: boolean;
      is_manditory: boolean;
      manditory_region: string;
      manditory_invoice_type: string;
      manditory_invoice_category: string;
      vmd_ref_1: string;
      vmd_ref_2: string;
      vmd_ref_3: string;
      po_header_ref_1: string;
      po_header_ref_2: string;
      po_header_ref_3: string;
      po_line_ref_1: string;
      po_line_ref_2: string;
      po_line_ref_3: string;
      rule_1: string;
      rule_2: string;
      rule_3: string;
      derived_field_1: string;
      derived_field_2: string;
      derived_field_3: string;
      derived_field_4: string;
      derived_field_5: string;
      is_anomaly_detection: boolean;
      anomaly_definition: string;
      region: string;
      region_nuance: string;
      threshold: string;
      policy: string;
    }>;
  }>> {
    return this.makeRequest('/api/admin/field-config');
  }

  async updateFieldConfig(fieldConfig: any): Promise<ApiResponse<{
    status: string;
    message: string;
  }>> {
    return this.makeRequest('/api/admin/field-config', {
      method: 'POST',
      body: JSON.stringify({ field_config: fieldConfig }),
    });
  }

  async getFeedbackData(): Promise<ApiResponse<{
    feedback: Array<{
      id: string;
      feedback_data: any;
    }>;
  }>> {
    return this.makeRequest('/api/admin/feedback');
  }

  async generateLearnings(feedbackData: {
    fieldName: string;
    originalValue: string;
    modifiedValue: string;
    textFeedback: string;
    screenshotUrl?: string;
  }): Promise<ApiResponse<{
    inputs: {
      fieldName: string;
      currentDescription: string;
      originalValue: string;
      modifiedValue: string;
      textFeedback: string;
      screenshotUrl?: string;
    };
    outputs: {
      diagnosis: string;
      suggested_description: string;
    };
    fieldConfigId: string;
  }>> {
    return this.makeRequest('/api/admin/generate-learnings', {
      method: 'POST',
      body: JSON.stringify(feedbackData),
    });
  }

  // Dashboard API methods
  async getDashboardInvoices(): Promise<ApiResponse<{
    invoices: Array<{
      invoice_id: string;
      filename: string;
      status: string;
      invoice_type: string;
      reception_date: string;
      due_date: string;
      priority: string;
      priority_reason: string;
    }>;
  }>> {
    return this.makeRequest('/api/dashboard/invoices', {}, 30000); // 30 second timeout
  }

  async getTopDashboardCases(): Promise<ApiResponse<{
    top_cases: Array<{
      invoice_id: string;
      filename: string;
      status: string;
      due_date: string;
      priority: string;
      priority_reason: string;
    }>;
  }>> {
    return this.makeRequest('/api/dashboard/top-cases', {}, 15000); // 15 second timeout
  }

  async getInvoiceMessages(fileName: string): Promise<ApiResponse<{
    messages: Array<{
      role: string;
      content: string;
      order: number;
      timestamp: string;
    }>;
  }>> {
    return this.makeRequest(`/api/invoice/${encodeURIComponent(fileName)}/messages`);
  }

  async storeInvoiceMessage(fileName: string, role: string, content: string, order: number): Promise<ApiResponse<{
    status: string;
    message: string;
  }>> {
    return this.makeRequest(`/api/invoice/${encodeURIComponent(fileName)}/messages`, {
      method: 'POST',
      body: JSON.stringify({ role, content, order }),
    });
  }

  async updateCriticalFields(
    fileName: string,
    fields: {
      headers: Array<{ key: string; value: string | number }>;
      items: Array<{ index: number; key: string; value: string | number }>;
    }
  ): Promise<ApiResponse<{
    status: string;
    message: string;
  }>> {
    return this.makeRequest(`/api/invoice/${encodeURIComponent(fileName)}/critical-fields`, {
      method: 'POST',
      body: JSON.stringify({ fields }),
    });
  }

  async revalidateInvoice(
    fileName: string,
    fields: {
      headers: Array<{ key: string; value: string | number }>;
      items: Array<{ index: number; key: string; value: string | number }>;
    }
  ): Promise<ApiResponse<{
    status: string;
    message: string;
    critical_fields_updated: boolean;
    revalidation_results: Array<{ role: string; content: string }>;
  }>> {
    return this.makeRequest(`/api/invoice/${encodeURIComponent(fileName)}/revalidate`, {
      method: 'POST',
      body: JSON.stringify({ fields }),
    }, 60000); // 60 second timeout for revalidation
  }

  // Validation Configuration API methods
  async getValidationRules(): Promise<ApiResponse<{
    validation_rules: Array<{
      id: string;
      name: string;
      type: string;
      enabled: boolean;
      description: string;
      configuration: Record<string, any>;
      operations: Array<{
        type: string;
        parameters: Record<string, any>;
        description: string;
      }>;
      created_at?: string;
      updated_at?: string;
    }>;
  }>> {
    return this.makeRequest('/api/admin/validation-rules');
  }

  async createValidationRule(rule: {
    name: string;
    type: string;
    enabled: boolean;
    description: string;
    configuration: Record<string, any>;
    operations: Array<{
      type: string;
      parameters: Record<string, any>;
      description: string;
    }>;
  }): Promise<ApiResponse<{
    status: string;
    message: string;
    rule_id: string;
  }>> {
    return this.makeRequest('/api/admin/validation-rules', {
      method: 'POST',
      body: JSON.stringify({ validation_rule: rule }),
    });
  }

  async updateValidationRule(ruleId: string, rule: {
    name: string;
    type: string;
    enabled: boolean;
    description: string;
    configuration: Record<string, any>;
    operations: Array<{
      type: string;
      parameters: Record<string, any>;
      description: string;
    }>;
  }): Promise<ApiResponse<{
    status: string;
    message: string;
  }>> {
    return this.makeRequest(`/api/admin/validation-rules/${encodeURIComponent(ruleId)}`, {
      method: 'PUT',
      body: JSON.stringify({ validation_rule: rule }),
    });
  }

  async deleteValidationRule(ruleId: string): Promise<ApiResponse<{
    status: string;
    message: string;
  }>> {
    return this.makeRequest(`/api/admin/validation-rules/${encodeURIComponent(ruleId)}`, {
      method: 'DELETE',
    });
  }

  async toggleValidationRule(ruleId: string, enabled: boolean): Promise<ApiResponse<{
    status: string;
    message: string;
  }>> {
    return this.makeRequest(`/api/admin/validation-rules/${encodeURIComponent(ruleId)}/toggle`, {
      method: 'POST',
      body: JSON.stringify({ enabled }),
    });
  }

}

export const apiService = new ApiService();